import { useState } from "react";
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import type { Window } from "@rua-watch/shared";
import { useOverview } from "../api/hooks";
import { HealthRow } from "../components/HealthRow";
import { Stat } from "../components/Stat";
import { TopConcerns } from "../components/TopConcerns";
import { WindowPicker } from "../components/WindowPicker";
import { formatInt, formatPct } from "../lib/format";

// Operations cockpit — answers "is anything broken?" and "what should
// I look at?" before getting into the per-domain detail. Layout:
//
//   1. Health row     (4 counters, deeplinked)
//   2. Stat cards     (totals with delta vs previous window)
//   3. Trend chart + Top concerns (2-column on wide screens)
//
// All data comes from a single /api/overview call so the page renders
// in one round-trip.

export function OverviewRoute() {
  const [window, setWindow] = useState<Window>("30d");
  const { data, isLoading, error } = useOverview("all", window);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold text-slate-900 dark:text-slate-100">Overview</h1>
        <WindowPicker value={window} onChange={setWindow} />
      </div>

      {isLoading ? (
        <p className="text-slate-500 dark:text-slate-400">Loading…</p>
      ) : null}
      {error ? (
        <p className="text-red-700 dark:text-red-300">Failed to load.</p>
      ) : null}

      {data ? (
        <>
          <HealthRow health={data.health} />

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-4">
            <Stat
              label="Messages"
              value={formatInt(data.totals.messages)}
              delta={makeCountDelta(data.totals.messages, data.previousTotals.messages)}
            />
            <Stat
              label="DMARC pass"
              value={
                data.totals.messages > 0
                  ? formatPct(data.totals.dmarcPass / data.totals.messages)
                  : "—"
              }
              hint={`${formatInt(data.totals.dmarcPass)} messages`}
              delta={makeRateDelta(
                rate(data.totals.dmarcPass, data.totals.messages),
                rate(data.previousTotals.dmarcPass, data.previousTotals.messages),
              )}
            />
            <Stat
              label="SPF aligned"
              value={
                data.totals.messages > 0
                  ? formatPct(data.totals.spfAligned / data.totals.messages)
                  : "—"
              }
              delta={makeRateDelta(
                rate(data.totals.spfAligned, data.totals.messages),
                rate(data.previousTotals.spfAligned, data.previousTotals.messages),
              )}
            />
            <Stat
              label="DKIM aligned"
              value={
                data.totals.messages > 0
                  ? formatPct(data.totals.dkimAligned / data.totals.messages)
                  : "—"
              }
              delta={makeRateDelta(
                rate(data.totals.dkimAligned, data.totals.messages),
                rate(data.previousTotals.dkimAligned, data.previousTotals.messages),
              )}
            />
          </div>

          <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
            <div className="rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-4 lg:col-span-2">
              <h2 className="text-sm font-medium text-slate-700 dark:text-slate-300">
                Volume & alignment
              </h2>
              <div className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={data.trend}>
                    <CartesianGrid strokeDasharray="3" stroke="#e2e8f0" className="dark:stroke-slate-700" />
                    <XAxis dataKey="day" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip
                      contentStyle={{
                        background: "var(--tooltip-bg, #fff)",
                        borderColor: "#e2e8f0",
                        fontSize: 12,
                      }}
                    />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    <Line
                      type="monotone"
                      dataKey="messages"
                      stroke="#0f172a"
                      dot={false}
                      name="Messages"
                    />
                    <Line
                      type="monotone"
                      dataKey="dmarcPass"
                      stroke="#16a34a"
                      dot={false}
                      name="DMARC pass"
                    />
                    <Line
                      type="monotone"
                      dataKey="spfAligned"
                      stroke="#2563eb"
                      dot={false}
                      name="SPF aligned"
                    />
                    <Line
                      type="monotone"
                      dataKey="dkimAligned"
                      stroke="#9333ea"
                      dot={false}
                      name="DKIM aligned"
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>

            <TopConcerns concerns={data.concerns} />
          </div>
        </>
      ) : null}
    </div>
  );
}

// Build a delta payload for a rate metric (DMARC/SPF/DKIM pass rate).
// Reports change in percentage points so the value is calibrated to
// the metric's scale. `polarity = "good-up"` because higher pass rate
// is unambiguously better.
function makeRateDelta(current: number, previous: number) {
  if (current === 0 && previous === 0) return undefined;
  const diff = current - previous;
  if (Math.abs(diff) < 0.001) {
    return { text: "stable vs prev window", direction: "neutral" as const, polarity: "good-up" as const };
  }
  return {
    text: `${(diff * 100).toFixed(1)}pp vs ${formatPct(previous)}`,
    direction: diff > 0 ? ("up" as const) : ("down" as const),
    polarity: "good-up" as const,
  };
}

// Volume delta. Higher message count isn't qualitatively good or bad —
// it just describes traffic. Render with neutral polarity so the
// arrow doesn't paint a value judgement on something that's just
// activity.
function makeCountDelta(current: number, previous: number) {
  if (previous === 0 && current === 0) return undefined;
  if (previous === 0) {
    return { text: "no data prev window", direction: "neutral" as const, polarity: "neutral" as const };
  }
  const diff = current - previous;
  const pct = (diff / previous) * 100;
  return {
    text: `${pct >= 0 ? "+" : ""}${pct.toFixed(0)}% vs ${formatInt(previous)}`,
    direction: diff > 0 ? ("up" as const) : diff < 0 ? ("down" as const) : ("neutral" as const),
    polarity: "neutral" as const,
  };
}

function rate(numer: number, denom: number): number {
  return denom > 0 ? numer / denom : 0;
}
